'use client';

import { useLocale } from 'next-intl';
import { useRef, type ReactElement } from 'react';
import { SKELETON_BLOCK_ATTR } from '@/lib/skeleton-morph';
import { useSkeletonMorphSource } from '@/components/ui/use-skeleton-morph';
import {
  CHAT_MESSAGE_SURFACE_BOUNDS_WIDTH_CLASS,
  CHAT_MESSAGE_READING_BOUNDS_INSET_X_CLASS,
} from '@/components/chat/chat-message-layout';
import { getOrgChatUiCopy } from '@/i18n/org-chat-ui-copy';
import { cn } from '@/lib/utils';

/** A line of text at rest: rounded, faint, no pulse. */
function Line({ className }: { className?: string }): ReactElement {
  return (
    <div className={cn('bg-foreground/[0.07] h-2.5 rounded-full', className)} />
  );
}

/**
 * Places a thread page's skeleton where its content will land: centred like
 * an opening exchange, or at the top when the thread was recorded as taller
 * than the column (thread-transcript-shape-hint, applied to <html> before
 * first paint). The parent must be a flex column with the centring height.
 */
export const THREAD_SKELETON_PLACEMENT_CLASS =
  'flex flex-1 flex-col justify-center [html[data-thread-skeleton~=tall]_&]:justify-start [html[data-thread-skeleton~=tall]_&]:pt-10';

/** Hidden when the recorded shape says the latest turn had no such part. */
const UNLESS_CARD =
  '[html[data-thread-skeleton~=known]:not([data-thread-skeleton~=card])_&]:hidden';
const UNLESS_ACTIONS =
  '[html[data-thread-skeleton~=known]:not([data-thread-skeleton~=actions])_&]:hidden';

/**
 * The shape of a thread's latest turn, drawn with the real turn's geometry
 * (user bubble, Work row, result card, reply, actions) so the page settles
 * into place instead of swapping a generic placeholder for a different
 * layout. It fades in only after a short delay, so a fast load never shows
 * it at all.
 */
export function ThreadTranscriptSkeleton({
  className,
  shapeAware = false,
}: {
  className?: string;
  /** Follow the loading thread's recorded shape (thread pages only). */
  shapeAware?: boolean;
}): ReactElement {
  const copy = getOrgChatUiCopy(useLocale());
  const rootRef = useRef<HTMLDivElement>(null);
  useSkeletonMorphSource(rootRef);
  // Each block names the real element it morphs onto (use-skeleton-morph).
  const block = (role: string) => ({ [SKELETON_BLOCK_ATTR]: role });
  return (
    <div
      ref={rootRef}
      role="status"
      aria-label={copy.loadingMessages}
      className={cn(
        'thread-skeleton-enter mx-auto w-full',
        CHAT_MESSAGE_SURFACE_BOUNDS_WIDTH_CLASS,
        className
      )}
    >
      <div
        aria-hidden="true"
        className={cn('space-y-6', CHAT_MESSAGE_READING_BOUNDS_INSET_X_CLASS)}
      >
        <div className="flex justify-end">
          <div
            {...block('user')}
            className="bg-foreground/5 w-3/5 space-y-2.5 rounded-[18px] px-3.5 py-3"
          >
            <Line className="w-full" />
            <Line className="w-2/3" />
          </div>
        </div>
        <div
          {...block('work')}
          className="flex h-6 items-center gap-2 rounded-md"
        >
          <span className="bg-foreground/[0.09] size-4 shrink-0 rounded-[5px]" />
          <Line className="w-28" />
          <Line className="ml-auto w-16" />
        </div>
        <div
          {...block('card')}
          className={cn(
            'border-border/35 overflow-hidden rounded-xl border',
            shapeAware && UNLESS_CARD
          )}
        >
          <div className="bg-foreground/[0.03] h-44" />
          <div className="flex h-11 items-center px-3">
            <Line className="w-40" />
          </div>
        </div>
        <div {...block('reply')} className="space-y-3 rounded-md">
          <Line className="w-full" />
          <Line className="w-11/12" />
          <Line className="w-4/5" />
          <Line className="w-1/2" />
        </div>
        <div
          {...block('actions')}
          className={cn('flex gap-3 rounded-md', shapeAware && UNLESS_ACTIONS)}
        >
          <Line className="w-10" />
          <Line className="w-12" />
        </div>
      </div>
    </div>
  );
}
